import React, { useState, useEffect } from 'react';
import { TrendingUp, Star, Award, BarChart3, RefreshCw, AlertCircle } from 'lucide-react';
import { stockService } from '../services/stockService';
import { useLanguage } from '../contexts/LanguageContext';

const StockRecommendation = ({ watchlist, onStockSelect }) => {
  const { t } = useLanguage();
  const [recommendations, setRecommendations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Scoring criteria weights
  const SCORING_WEIGHTS = {
    performance: 0.25,      // Price performance
    momentum: 0.20,         // Technical momentum
    value: 0.20,           // Valuation metrics
    volume: 0.15,          // Trading volume
    volatility: 0.10,      // Risk factor (lower is better)
    trend: 0.10           // Short-term trend
  };

  useEffect(() => {
    if (watchlist.length > 0) {
      analyzeWatchlist();
    }
  }, [watchlist]);

  const analyzeWatchlist = async () => {
    if (watchlist.length === 0) return;
    
    setLoading(true);
    setError(null);
    
    try {
      const stockAnalyses = await Promise.all(
        watchlist.map(async (stock) => {
          try {
            // Get quote data
            const quote = await stockService.getQuote(stock.symbol);
            
            // Validate quote data
            if (!quote || typeof quote !== 'object') {
              throw new Error('Invalid quote data received');
            }
            
            // Get historical data for analysis
            const historicalData = await stockService.getHistoricalData(stock.symbol, 'daily', '3mo');
            
            // Validate historical data
            if (!Array.isArray(historicalData) || historicalData.length === 0) {
              throw new Error('Invalid or empty historical data');
            }
            
            // Calculate score
            const score = calculateStockScore(quote, historicalData);
            
            return {
              ...stock,
              quote,
              score,
              analysis: generateAnalysis(quote, historicalData, score)
            };
          } catch (error) {
            console.error(`Error analyzing ${stock.symbol}:`, error);
            return {
              ...stock,
              score: 0,
              quote: null,
              analysis: {
                recommendation: 'ERROR',
                reasons: ['Analysis failed'],
                risks: ['Unable to fetch data']
              },
              error: error.message
            };
          }
        })
      );

      // Filter out error cases and sort by score
      const validAnalyses = stockAnalyses.filter(stock => !stock.error);
      const sortedStocks = validAnalyses.sort((a, b) => b.score - a.score);
      
      setRecommendations(sortedStocks);
      setLastUpdated(new Date());
    } catch (error) {
      setError('Failed to analyze watchlist stocks');
      console.error('Analysis error:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStockScore = (quote, historicalData) => {
    if (!quote || !historicalData.length) return 0;

    let score = 0;
    const latest = historicalData[historicalData.length - 1];
    const weekAgo = historicalData[Math.max(0, historicalData.length - 5)];
    const monthAgo = historicalData[Math.max(0, historicalData.length - 20)];

    // Performance Score (25%)
    const weekPerformance = weekAgo ? ((latest.close - weekAgo.close) / weekAgo.close) * 100 : 0;
    const monthPerformance = monthAgo ? ((latest.close - monthAgo.close) / monthAgo.close) * 100 : 0;
    const performanceScore = Math.max(0, Math.min(100, (weekPerformance + monthPerformance) * 2 + 50));
    score += performanceScore * SCORING_WEIGHTS.performance;

    // Momentum Score (20%) - Based on recent price action
    const recentPrices = historicalData.slice(-10).map(d => d.close);
    const momentum = calculateMomentum(recentPrices);
    const momentumScore = Math.max(0, Math.min(100, momentum * 10 + 50));
    score += momentumScore * SCORING_WEIGHTS.momentum;

    // Value Score (20%) - Based on P/E ratio and other metrics
    const pe = typeof quote.pe === 'number' ? quote.pe : 0;
    let valueScore = 50; // Neutral score
    if (pe > 0 && pe < 15) valueScore = 80; // Undervalued
    else if (pe >= 15 && pe < 25) valueScore = 60; // Fair value
    else if (pe >= 25) valueScore = 30; // Overvalued
    score += valueScore * SCORING_WEIGHTS.value;

    // Volume Score (15%) - Based on average volume
    const avgVolume = typeof quote.avgVolume === 'number' ? quote.avgVolume : 0;
    const currentVolume = typeof quote.volume === 'number' ? quote.volume : 0;
    const volumeRatio = avgVolume > 0 ? currentVolume / avgVolume : 1;
    const volumeScore = Math.min(100, volumeRatio * 50);
    score += volumeScore * SCORING_WEIGHTS.volume;

    // Volatility Score (10%) - Lower volatility is better for recommendations
    const volatility = calculateVolatility(historicalData.slice(-20));
    const volatilityScore = Math.max(0, 100 - volatility * 5);
    score += volatilityScore * SCORING_WEIGHTS.volatility;

    // Trend Score (10%) - Short-term trend direction
    const trendScore = calculateTrendScore(historicalData.slice(-5));
    score += trendScore * SCORING_WEIGHTS.trend;

    return Math.round(Math.max(0, Math.min(100, score)));
  };

  const calculateMomentum = (prices) => {
    if (prices.length < 2) return 0;
    let momentum = 0;
    for (let i = 1; i < prices.length; i++) {
      const change = (prices[i] - prices[i-1]) / prices[i-1];
      momentum += change;
    }
    return momentum / (prices.length - 1);
  };

  const calculateVolatility = (data) => {
    if (data.length < 2) return 0;
    const returns = [];
    for (let i = 1; i < data.length; i++) {
      returns.push((data[i].close - data[i-1].close) / data[i-1].close);
    }
    const mean = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance = returns.reduce((sum, ret) => sum + Math.pow(ret - mean, 2), 0) / returns.length;
    return Math.sqrt(variance) * Math.sqrt(252) * 100; // Annualized volatility
  };

  const calculateTrendScore = (data) => {
    if (data.length < 2) return 50;
    const firstPrice = data[0].close;
    const lastPrice = data[data.length - 1].close;
    const trendChange = ((lastPrice - firstPrice) / firstPrice) * 100;
    return Math.max(0, Math.min(100, trendChange * 10 + 50));
  };

  const generateAnalysis = (quote, historicalData, score) => {
    const analysis = {
      recommendation: 'HOLD',
      reasons: [],
      risks: []
    };

    if (score >= 75) {
      analysis.recommendation = 'STRONG BUY';
      analysis.reasons.push('Excellent overall score');
    } else if (score >= 60) {
      analysis.recommendation = 'BUY';
      analysis.reasons.push('Good fundamental and technical indicators');
    } else if (score >= 40) {
      analysis.recommendation = 'HOLD';
      analysis.reasons.push('Mixed signals, moderate performance');
    } else {
      analysis.recommendation = 'WEAK';
      analysis.reasons.push('Below average performance metrics');
    }

    // Add specific reasons based on metrics
    if (typeof quote.changePercent === 'number' && quote.changePercent > 5) {
      analysis.reasons.push('Strong recent performance');
    }
    if (typeof quote.pe === 'number' && quote.pe > 0 && quote.pe < 15) {
      analysis.reasons.push('Attractive valuation');
    }
    if (typeof quote.volume === 'number' && typeof quote.avgVolume === 'number' && 
        quote.avgVolume > 0 && quote.volume > quote.avgVolume * 1.5) {
      analysis.reasons.push('High trading interest');
    }

    // Add risks
    if (typeof quote.changePercent === 'number' && quote.changePercent < -10) {
      analysis.risks.push('Recent decline');
    }
    if (typeof quote.pe === 'number' && quote.pe > 30) {
      analysis.risks.push('High valuation');
    }

    return analysis;
  };

  const getRecommendationColor = (recommendation) => {
    switch (recommendation) {
      case 'STRONG BUY': return 'text-green-700 bg-green-100';
      case 'BUY': return 'text-green-600 bg-green-50';
      case 'HOLD': return 'text-yellow-600 bg-yellow-50';
      default: return 'text-red-600 bg-red-50';
    }
  };

  const getScoreColor = (score) => {
    if (score >= 75) return 'text-green-700';
    if (score >= 60) return 'text-green-600';
    if (score >= 40) return 'text-yellow-600';
    return 'text-red-600';
  };

  if (watchlist.length === 0) {
    return (
      <div className="card">
        <div className="card-title">
          <Award size={20} />
          {t('stockRecommendations')}
        </div>
        <div className="recommendation-empty">
          <TrendingUp size={48} className="text-gray-400" />
          <p className="text-gray-600">Add stocks to your watchlist to get personalized recommendations</p>
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-title">
        <Award size={20} />
        Stock Recommendations
        <div className="recommendation-actions">
          <button
            onClick={analyzeWatchlist}
            disabled={loading}
            className="recalculate-btn"
            title="Recalculate stock rankings with latest data"
          >
            <RefreshCw size={16} className={loading ? 'spinning' : ''} />
            {loading ? t('loading') : t('recalculateRankings')}
          </button>
        </div>
      </div>

      {error && (
        <div className="alert alert-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      {loading && (
        <div className="recommendation-loading">
          <div className="loading-spinner"></div>
          <p>{t('loading')} {watchlist.length} {t('stockRecommendations')}...</p>
        </div>
      )}

      {recommendations.length > 0 && (
        <div className="recommendations-container">
          {lastUpdated && (
            <div className="recommendations-header">
              <p className="text-sm text-gray-600">
                {t('lastUpdated')}: {lastUpdated.toLocaleTimeString()}
              </p>
            </div>
          )}

          <div className="recommendations-list">
            {recommendations.slice(0, 5).map((stock, index) => (
              <div
                key={stock.symbol}
                className="recommendation-item"
                onClick={() => onStockSelect(stock)}
              >
                <div className="recommendation-rank">
                  {index === 0 && <Star size={16} className="text-yellow-500" />}
                  <span className="rank-number">#{index + 1}</span>
                </div>

                <div className="recommendation-info">
                  <div className="recommendation-header">
                    <h4 className="stock-symbol">{stock.symbol}</h4>
                    <div className={`recommendation-badge ${getRecommendationColor(stock.analysis?.recommendation || 'HOLD')}`}>
                      {stock.analysis?.recommendation || 'HOLD'}
                    </div>
                  </div>
                  
                  <p className="stock-name">{stock.name}</p>
                  
                  <div className="recommendation-metrics">
                    <div className="metric">
                      <span className="metric-label">{t('performanceScore')}:</span>
                      <span className={`metric-value ${getScoreColor(stock.score)}`}>
                        {stock.score}/100
                      </span>
                    </div>
                    
                    {stock.quote && (
                      <>
                        <div className="metric">
                          <span className="metric-label">{t('price')}:</span>
                          <span className="metric-value">
                            ${typeof stock.quote.price === 'number' ? stock.quote.price.toFixed(2) : 'N/A'}
                          </span>
                        </div>
                        
                        <div className="metric">
                          <span className="metric-label">{t('change')}:</span>
                          <span className={`metric-value ${
                            (typeof stock.quote.changePercent === 'number' && stock.quote.changePercent >= 0) ? 'text-green-600' : 'text-red-600'
                          }`}>
                            {typeof stock.quote.changePercent === 'number' ? (
                              <>
                                {stock.quote.changePercent >= 0 ? '+' : ''}
                                {stock.quote.changePercent.toFixed(2)}%
                              </>
                            ) : 'N/A'}
                          </span>
                        </div>
                      </>
                    )}
                  </div>

                  <div className="recommendation-reasons">
                    {stock.analysis && Array.isArray(stock.analysis.reasons) && 
                     stock.analysis.reasons.slice(0, 2).map((reason, idx) => (
                      <span key={idx} className="reason-tag">
                        {reason}
                      </span>
                    ))}
                  </div>
                </div>

                <div className="recommendation-chart">
                  <BarChart3 size={20} className="text-gray-400" />
                </div>
              </div>
            ))}
          </div>

          {recommendations.length > 5 && (
            <div className="recommendations-footer">
              <p className="text-sm text-gray-600">
                Showing top 5 of {recommendations.length} analyzed stocks
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default StockRecommendation;